import JsBarcode from 'jsbarcode';

/**
 * Generate door item number (article number) based on door specifications
 * Format: HES-{PRODUCT}-{WIDTH}-{HEIGHT}-GL{XX}-GW{XXXX}-GH{XXXX}-W{X}-{THRESHOLD}-L{XX}-S{X}-FP{X}-H{TYPE}{COUNT}-{HAND}-{STATUS}-{PAINT}-GR{XXX}-K{X}-{S/X}
 */
export const generateItemNumber = (doorMetadata) => {
  // Map door types to product codes
  const productMapping = {
    'door_60mm': '60O',
    'door_60mm_outswing': '60O',
    'door_60mm_inswing': '60I',
    'door_meranti': 'MER',
    'door_cottage': 'COT',
    'door_18deg': '18D',
    '60O': '60O',
    '60I': '60I',
    'MER': 'MER',
    'COT': 'COT',
    '18D': '18D'
  };

  // Map lock types to codes
  const lockMapping = {
    'none': 'L00',
    'None': 'L00',
    '565 analoge': 'L01',
    '8765 analoge': 'L02',
    '2150-3PL analoge': 'L03',
    'ASSA 8765': 'L04',
    // Legacy mappings for backwards compatibility
    'Assa': 'L04',
    '3-Point Lock': 'L03',
    '3PL': 'L03'
  };

  // Map striker types to codes
  const strikerMapping = {
    'none': 'S0',
    'None': 'S0',
    '2860-1ZN': 'S1',
    'Scanlock 4690': 'S2',
    '3PL strike': 'S3',
    '1887-2': 'S4',
    '1220/9 adjust': 'S5',
    'ASSA 1887-2': 'S6',
    '': 'S0' // Default to none
  };

  // Map frame paint options to codes
  const framePaintMapping = {
    'none': 'FP0',
    'None': 'FP0',
    'FP-3': 'FP3',
    'FP-4': 'FP4',
    'Painted visible sides': 'FP3',
    'Painted all sides': 'FP4',
    '': 'FP0' // Default to none
  };

  // Map threshold types to codes
  const thresholdMapping = {
    'Pine': 'TP',
    'Oak': 'TO',
    'ACCOYA': 'TAC',
    'Alu w holes': 'TAH',
    'Alu-no holes': 'TAN',
    // Legacy mappings for backwards compatibility
    'alu': 'TAH',
    'aluminium': 'TAH',
    'oak': 'TO',
    'pine': 'TP',
    'TA': 'TAH',
    '': 'TAH' // Default to aluminium with holes
  };

  // Map surface finish to paint type codes
  const paintTypeMapping = {
    'R': 'R',
    'N': 'N',
    'ST': 'ST',
    'RAL': 'R',
    'NCS': 'N',
    'Stain': 'ST'
  };

  // Map handedness to codes
  const handMapping = {
    'Left': 'L',
    'Right': 'R',
    'L': 'L',
    'R': 'R'
  };

  // Map door status (for double doors)
  const statusMapping = {
    'Active': 'A',
    'Passive': 'P',
    'A': 'A',
    'P': 'P'
  };

  // Map hinge type to codes
  const hingeTypeMapping = {
    '2': '2',
    '5': '5',
    'Standard': '2',
    'Tectus': '5',
    'H2': '2',
    'H5': '5'
  };

  // Extract and format values
  const product = productMapping[doorMetadata.doorName] || '60O';
  const width = String(doorMetadata.width || 988).padStart(4, '0');
  const height = String(doorMetadata.height || 2078).padStart(4, '0');
  
  // Glass
  const glassStyle = String(doorMetadata.glassStyle || (doorMetadata.hasGlass ? '01' : '00')).padStart(2, '0');
  const glassWidth = String(doorMetadata.glassWidth || 0).padStart(4, '0');
  const glassHeight = String(doorMetadata.glassHeight || 0).padStart(4, '0');
  
  // Features
  const waternose = doorMetadata.waternose ? '1' : '0';
  const threshold = thresholdMapping[doorMetadata.thresholdType] || 'TAH';
  const lock = lockMapping[doorMetadata.lockType] || 'L01';
  const striker = strikerMapping[doorMetadata.strikerType] || 'S0';
  const framePaint = framePaintMapping[doorMetadata.framePaint] || 'FP0';
  
  // Hinges
  const hingeType = hingeTypeMapping[doorMetadata.hingeType] || '2';
  const hingeCount = String(doorMetadata.hingeCount || 2);
  const hinge = `H${hingeType}${hingeCount}`;
  
  // Hand and status
  const hand = handMapping[doorMetadata.handedness] || 'L';
  const status = statusMapping[doorMetadata.doorStatus] || 'A';
  
  // Paint
  const paintType = paintTypeMapping[doorMetadata.surfaceFinish] || 'R';
  const paintCode = (doorMetadata.colorCode || '7016').toUpperCase().replace(/-/g, '');
  const paint = `${paintType}${paintCode}`;
  
  // Groove
  const groove = String(doorMetadata.grooveDesign || 0).padStart(3, '0');
  
  // Kickplate
  const kickplate = doorMetadata.kickplate ? '1' : '0';
  
  // Special feature
  const special = doorMetadata.specialFeature ? 'X' : 'S';
  
  // Build the item number with new striker and frame paint fields
  const itemNumber = `HES-${product}-${width}-${height}-GL${glassStyle}-GW${glassWidth}-GH${glassHeight}-W${waternose}-${threshold}-L${lock}-${striker}-${framePaint}-${hinge}-${hand}-${status}-${paint}-GR${groove}-K${kickplate}-${special}`;
  
  return itemNumber;
};

/**
 * Generate barcode image as data URL
 * @param {string} itemNumber - The item number to encode
 * @param {object} options - JsBarcode options
 * @returns {string} Data URL of the barcode image
 */
export const generateBarcodeDataUrl = (itemNumber, options = {}) => {
  const canvas = document.createElement('canvas');
  
  const defaultOptions = {
    format: 'CODE128',
    width: 2,
    height: 100,
    displayValue: true,
    fontSize: 14,
    margin: 10,
    ...options
  };
  
  try {
    JsBarcode(canvas, itemNumber, defaultOptions);
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating barcode:', error);
    return null;
  }
};

/**
 * Generate barcode as Blob for file upload
 * @param {string} itemNumber - The item number to encode
 * @returns {Promise<Blob>} Barcode image as Blob
 */
export const generateBarcodeBlob = async (itemNumber) => {
  const dataUrl = generateBarcodeDataUrl(itemNumber);
  
  if (!dataUrl) {
    throw new Error('Failed to generate barcode');
  }
  
  // Convert data URL to Blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  
  return blob;
};

/**
 * Parse item number into components for display
 * @param {string} itemNumber - The item number to parse
 * @returns {object} Parsed components
 */
export const parseItemNumber = (itemNumber) => {
  if (!itemNumber || !itemNumber.startsWith('HES-')) {
    return null;
  }
  
  const parts = itemNumber.split('-');
  
  return {
    prefix: parts[0], // HES
    product: parts[1], // 60O, 60I, MER, COT, 18D
    width: parts[2], // 0988
    height: parts[3], // 2078
    glass: parts[4], // GL00
    glassWidth: parts[5], // GW0000
    glassHeight: parts[6], // GH0000
    waternose: parts[7], // W0 or W1
    threshold: parts[8], // TAH, TAN, TO, TP, TAC
    lock: parts[9], // L00, L01, L02, L03, L04
    striker: parts[10], // S0, S1, S2, S3, S4, S5, S6
    framePaint: parts[11], // FP0, FP3, FP4
    hinge: parts[12], // H22, H25, H32, etc.
    hand: parts[13], // L or R
    status: parts[14], // A or P
    paint: parts[15], // R7016, NS0502Y, STWAL
    groove: parts[16], // GR000
    kickplate: parts[17], // K0 or K1
    special: parts[18] // S or X
  };
};

/**
 * Get human-readable description of item number components
 * @param {string} itemNumber - The item number
 * @returns {string} Human-readable description
 */
export const getItemNumberDescription = (itemNumber) => {
  const parsed = parseItemNumber(itemNumber);
  
  if (!parsed) {
    return 'Invalid item number';
  }
  
  const productNames = {
    '60O': '60mm Outswing',
    '60I': '60mm Inswing',
    'MER': 'Meranti',
    'COT': 'Cottage',
    '18D': '18°'
  };
  
  const thresholdNames = {
    'TAH': 'Alu w holes',
    'TAN': 'Alu-no holes',
    'TO': 'Oak',
    'TP': 'Pine',
    'TAC': 'ACCOYA',
    'TA': 'Aluminium' // Legacy
  };
  
  const lockNames = {
    'L00': 'None',
    'L01': '565 analoge',
    'L02': '8765 analoge',
    'L03': '2150-3PL analoge',
    'L04': 'ASSA 8765'
  };
  
  const strikerNames = {
    'S0': 'None',
    'S1': '2860-1ZN',
    'S2': 'Scanlock 4690',
    'S3': '3PL strike',
    'S4': '1887-2',
    'S5': '1220/9 adjust',
    'S6': 'ASSA 1887-2'
  };
  
  const framePaintNames = {
    'FP0': 'None',
    'FP3': 'Painted visible sides',
    'FP4': 'Painted all sides'
  };
  
  const parts = [];
  parts.push(`${productNames[parsed.product] || parsed.product} Door`);
  parts.push(`${parseInt(parsed.width)}×${parseInt(parsed.height)}mm`);
  
  if (parsed.glass !== 'GL00') {
    parts.push(`Glass Style ${parsed.glass.replace('GL', '')}`);
  }
  
  parts.push(`${parsed.hand === 'L' ? 'Left' : 'Right'} Hand`);
  parts.push(`${parsed.status === 'A' ? 'Active' : 'Passive'}`);
  parts.push(`${lockNames[parsed.lock] || 'Lock'}`);
  parts.push(`${thresholdNames[parsed.threshold] || 'Threshold'}`);
  
  if (parsed.waternose === 'W1') {
    parts.push('Waternose');
  }
  
  if (parsed.kickplate === 'K1') {
    parts.push('Kickplate');
  }
  
  if (parsed.special === 'X') {
    parts.push('Special Features');
  }
  
  return parts.join(' • ');
};

export default {
  generateItemNumber,
  generateBarcodeDataUrl,
  generateBarcodeBlob,
  parseItemNumber,
  getItemNumberDescription
};
